import { ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';
import Button from './Button';

/* ------------------------------------------------------------------ */
/* Skeleton — quiet hairline blocks, no shimmer                       */
/* ------------------------------------------------------------------ */

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
`;

export const Skeleton = styled.div<{ $w?: string; $h?: string }>`
  width: ${({ $w }) => $w ?? '100%'};
  height: ${({ $h }) => $h ?? '14px'};
  border-radius: var(--screening-r-input);
  background: var(--screening-hairline);
  animation: ${pulse} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* ------------------------------------------------------------------ */
/* Empty state — instruction + resolving action                       */
/* ------------------------------------------------------------------ */

const Frame = styled.div`
  border: 1px solid var(--screening-hairline);
  border-radius: var(--screening-r-card);
  background: var(--screening-paper);
  padding: 40px 32px;
  max-width: 520px;
`;

const Eyebrow = styled.p`
  margin: 0 0 10px;
  font-family: var(--screening-font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--screening-muted);
`;

const Title = styled.h3`
  margin: 0 0 8px;
  font-family: var(--screening-font-display);
  font-size: 18px;
  font-weight: 700;
  color: var(--screening-ink);
`;

const Body = styled.p`
  margin: 0 0 20px;
  font-size: 15px;
  line-height: 23px;
  color: var(--screening-muted);
`;

interface EmptyStateProps {
  eyebrow?: string;
  title: string;
  body?: string;
  action?: { label: string; onClick: () => void };
  children?: ReactNode;
}

export const EmptyState = ({
  eyebrow,
  title,
  body,
  action,
  children,
}: EmptyStateProps) => (
  <Frame>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <Title>{title}</Title>
    {body && <Body>{body}</Body>}
    {action && <Button onClick={action.onClick}>{action.label}</Button>}
    {children}
  </Frame>
);

/* ------------------------------------------------------------------ */
/* Error state — plain, product-voice, retry                          */
/* ------------------------------------------------------------------ */

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
}

export const ErrorState = ({
  title = 'That did not load',
  message = 'Something interrupted the request. Try again in a moment.',
  onRetry,
}: ErrorStateProps) => (
  <Frame>
    <Title>{title}</Title>
    <Body>{message}</Body>
    {onRetry && (
      <Button variant="secondary" onClick={onRetry}>
        Try again
      </Button>
    )}
  </Frame>
);
